/**
 * One reconcile pass: what the reasoner decided, and what the executor then told
 * the interviewer to do.
 *
 * The two halves are drawn apart on purpose. `verdict` is the MODEL's output;
 * `directive` is the executor-STAMPED `TurnDirective` as committed to
 * `/directive` — and `/directive` is overwritten on every reconcile, so this
 * column is the only directive HISTORY that exists (plan §4, R0-12's DDL note).
 * Reading them as one blob is how a stamped field gets attributed to the model.
 *
 * PD-3 owns the deep reconcile trace (per-pass claim → model → latency →
 * commit/superseded/failed → resulting `/match_events` node) and the state
 * inspector. This card carries the summary the TRANSCRIPT needs, and links out
 * of the raw row for the rest.
 */
import { Badge, Empty, Field, RawJson, type Tone } from './ui';
import { formatClock, type PassOutcome, type VerdictView } from './trace';

const OUTCOME_TONE: Record<PassOutcome, Tone> = {
  committed: 'green',
  superseded: 'amber',
  failed: 'red',
  no_commit: 'gray',
};

const OUTCOME_LABEL: Record<PassOutcome, string> = {
  committed: 'committed',
  superseded: 'superseded',
  failed: 'failed',
  // A pass that neither committed, was superseded, nor errored: a greeting pass
  // or one whose turn the executor chose not to write.
  no_commit: 'no commit',
};

export function OutcomeBadge({ outcome }: { outcome: PassOutcome }) {
  return <Badge tone={OUTCOME_TONE[outcome]}>{OUTCOME_LABEL[outcome]}</Badge>;
}

export function SubmitBadge({ action }: { action: string }) {
  if (action === 'none') return <Badge tone="gray">no submit</Badge>;
  return <Badge tone="purple">{action}</Badge>;
}

export default function VerdictCard({ verdict }: { verdict: VerdictView }) {
  const { directive, decision } = verdict;
  return (
    <div className="border border-gray-200 rounded-md bg-gray-50 p-3 space-y-3">
      {/* Pass identity */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <OutcomeBadge outcome={verdict.outcome} />
        {verdict.model && <Badge tone="blue">{verdict.model}</Badge>}
        {verdict.escalated && <Badge tone="amber" title="re-run on the escalation tier">escalated</Badge>}
        {verdict.latencyMs !== null && <span className="text-gray-600">{verdict.latencyMs} ms</span>}
        {(verdict.inputTokens !== null || verdict.outputTokens !== null) && (
          <span className="text-gray-600">
            {verdict.inputTokens ?? '?'} in / {verdict.outputTokens ?? '?'} out
          </span>
        )}
        <span className="text-gray-500">{formatClock(verdict.createdAt)}</span>
      </div>

      {verdict.error && (
        <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap">
          {verdict.error}
        </div>
      )}

      <div className="text-xs text-gray-500 font-mono break-all">
        based on {verdict.basedOnMessageKey ?? '—'} → committed {verdict.messageKey ?? '—'}
      </div>

      {/* What the reasoner decided */}
      {decision && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Decision">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone="gray">{decision.storyType ?? 'unknown'}</Badge>
              <SubmitBadge action={decision.submit?.action ?? 'none'} />
              {decision.requestSystemTurn && <Badge tone="blue">system turn requested</Badge>}
              {decision.distress && <Badge tone="red">distress</Badge>}
              {decision.permanentDots > 0 && <Badge tone="purple">{decision.permanentDots} dot(s)</Badge>}
            </div>
            {decision.submit?.reason && (
              <div className="mt-1 text-xs text-gray-600">{decision.submit.reason}</div>
            )}
            {decision.reclassification && (
              <div className="mt-1 text-xs text-amber-800">
                reclassified {decision.reclassification.from} → {decision.reclassification.to}:{' '}
                {decision.reclassification.reason}
              </div>
            )}
            {decision.safetyNote && <div className="mt-1 text-xs text-red-700">{decision.safetyNote}</div>}
          </Field>

          <Field label="Coverage">
            {decision.coverage.total === 0 ? (
              <Empty>no coverage map (untyped pebble)</Empty>
            ) : (
              <>
                <div className="text-xs text-gray-700">
                  {decision.coverage.covered}/{decision.coverage.total} covered · asked{' '}
                  {decision.askState.asked} · answered {decision.askState.answered} · declined{' '}
                  {decision.askState.declined}
                </div>
                {decision.coverage.missing.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {decision.coverage.missing.map((k) => (
                      <Badge key={k} tone="amber">
                        {k}
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            )}
          </Field>
        </div>
      )}

      {/* What the executor told the interviewer to do */}
      {directive ? (
        <div className="space-y-2">
          <Field label="Directive (stamped)">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone="blue">{directive.mode ?? 'unknown mode'}</Badge>
              <Badge tone="gray">{directive.storyType ?? 'unknown'}</Badge>
              {directive.status && (
                <Badge tone={directive.status.narrate ? 'purple' : 'gray'}>
                  status: {directive.status.kind}
                  {directive.status.narrate ? ' · narrate' : ''}
                </Badge>
              )}
              {directive.picker && <Badge tone="amber">picker: {directive.picker.field}</Badge>}
              {/* A directive may carry several CTAs of the SAME action — two
                  `go_to_match` entries differ only by `match_id` — so the
                  action alone is neither a unique key nor enough to tell the
                  two badges apart on screen. */}
              {directive.ctas.map((cta, i) => (
                <Badge key={`${i}-${cta.action}-${cta.matchId ?? ''}`} tone="green" title={cta.emitWhen}>
                  cta: {cta.action}
                  {cta.matchId ? ` → ${cta.matchId}` : ''}
                </Badge>
              ))}
              {directive.dotCount > 0 && <Badge tone="purple">{directive.dotCount} dot(s)</Badge>}
            </div>
            {directive.status?.textHint && (
              <div className="mt-1 text-xs text-gray-600">hint: “{directive.status.textHint}”</div>
            )}
            {directive.phaseNote && <div className="mt-1 text-xs text-gray-600">{directive.phaseNote}</div>}
          </Field>

          {directive.objectives.length > 0 && (
            <Field label={`Objectives (${directive.objectives.length})`}>
              <ul className="space-y-1">
                {/* `(unkeyed)` is what a malformed objective narrows to, and two
                    of them are not two of the same objective. */}
                {directive.objectives.map((o, i) => (
                  <li key={`${i}-${o.key}`} className="text-xs">
                    <code className="text-gray-700">{o.key}</code>
                    {o.oneAsk && <span className="ml-1 text-gray-500">· one ask</span>}
                    {o.privacySoft && <span className="ml-1 text-gray-500">· privacy-soft</span>}
                    {o.goal && <span className="ml-2 text-gray-600">{o.goal}</span>}
                  </li>
                ))}
              </ul>
            </Field>
          )}

          {directive.refinementQuestions.length > 0 && (
            <Field label="Refinement questions">
              <ol className="list-decimal pl-5 text-xs text-gray-700 space-y-0.5">
                {directive.refinementQuestions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ol>
            </Field>
          )}

          {directive.doNotAsk.length > 0 && (
            <Field label="Do not ask">
              <div className="flex flex-wrap gap-1">
                {directive.doNotAsk.map((k, i) => (
                  <Badge key={`${i}-${k}`} tone="red">
                    {k}
                  </Badge>
                ))}
              </div>
            </Field>
          )}
        </div>
      ) : (
        <Field label="Directive (stamped)">
          <Empty>none stored for this pass</Empty>
        </Field>
      )}

      <RawJson value={verdict.raw} label="raw verdict_archive row" />
    </div>
  );
}
